import axios from 'axios';
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, X, TrendingUp } from "lucide-react";
import { formatCurrency } from "@/lib/config";
import api from "@/lib/api";

export const RevenuDetailModal = ({ open, onClose, departement, year, month }) => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [totalTTC, setTotalTTC] = useState(0);
  const [totalHT, setTotalHT] = useState(0);

  useEffect(() => {
    if (open && departement) {
      loadTransactions();
    }
  }, [open, departement, year, month]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${api.baseUrl}/revenues/by-department/${year}/${month}?departement=${departement}`
      );
      const data = response.data;

      setTransactions(data.transactions || []);
      setTotalTTC(data.total_ttc || 0);
      setTotalHT(data.total_ht || 0);
    } catch (error) {
      console.error("Erreur chargement revenus:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDeptLabel = (dept) => {
    const labels = {
      "PRQC": "Peinture Résidentielle",
      "LMDS": "Le Marché du Store",
      "LOCATION": "Location Équipements",
      "GLOBAL": "Tous départements"
    };
    return labels[dept] || dept;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <span>Revenus - {getDeptLabel(departement)}</span>
            </div>
            <button
              onClick={onClose}
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
          </DialogTitle>
          <DialogDescription>
            {transactions.length} transaction{transactions.length > 1 ? 's' : ''} • 
            HT: {formatCurrency(totalHT)} • 
            TTC: {formatCurrency(totalTTC)}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-green-600" />
          </div>
        ) : (
          <div className="mt-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="py-2 px-3 text-left font-semibold text-slate-600">Date</th>
                    <th className="py-2 px-3 text-left font-semibold text-slate-600">Type</th>
                    <th className="py-2 px-3 text-left font-semibold text-slate-600">Client / Description</th>
                    <th className="py-2 px-3 text-left font-semibold text-slate-600">Compte</th>
                    <th className="py-2 px-3 text-center font-semibold text-slate-600">Type Revenu</th>
                    <th className="py-2 px-3 text-right font-semibold text-slate-600">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx, idx) => (
                    <tr key={tx.id || tx._id || idx} className="border-b border-slate-100 hover:bg-green-50">
                      <td className="py-2 px-3 text-slate-600">
                        {new Date(tx.date).toLocaleDateString('fr-CA')}
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className="text-xs">
                          {tx.type}
                        </Badge>
                      </td>
                      <td className="py-2 px-3">
                        <div className="max-w-md">
                          <p className="font-medium text-slate-900 truncate">
                            {tx.name || "—"}
                          </p>
                          {tx.memo && (
                            <p className="text-xs text-slate-500 truncate">
                              {tx.memo}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-600 font-mono">
                        {tx.account}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <Badge 
                          variant="outline"
                          className={tx.type_revenu === "Intérêts" ? "bg-blue-50 text-blue-700" : "bg-green-50 text-green-700"}
                        >
                          {tx.type_revenu}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 text-right font-medium">
                        <div className="text-green-600">
                          {formatCurrency(tx.montant_ht)}
                        </div>
                        <div className="text-xs text-slate-500">
                          TTC: {formatCurrency(tx.montant_ttc)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 font-bold">
                    <td colSpan="5" className="py-3 px-3 text-right text-slate-900">
                      TOTAL HT
                    </td>
                    <td className="py-3 px-3 text-right">
                      <div className="text-green-600">
                        {formatCurrency(totalHT)}
                      </div>
                      <div className="text-xs text-slate-500">
                        TTC: {formatCurrency(totalTTC)}
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
